import { Formik, useFormikContext, type FormikProps } from 'formik'
import React, {
  PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Heading6 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { useCategorySizesReadContext } from '@utils/CategorySizesContext'

import { ClientTestDto } from '@model/ClientTestDto'
import { DocumentClientAreaDto } from '@model/DocumentClientAreaDto'
import { TestFormValues } from '@model/Test'
import { TestSectionDto } from '@model/TestSectionsDto'

import { AnimatedSwitch } from '@shared/animations/AnimatedSwitch'
import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import {
  QuestionFieldFactory,
  getCleanedAnswers,
  validateTestForm,
} from '@shared/templates/Test/TestFields/TestFields'

import styles from './TestForm.module.scss'

export type TestFormRef = {
  goPrevStep(): void
}

interface BaseProps {
  data: ClientTestDto
  step: string
  centered?: boolean
  contentRef?: React.RefObject<HTMLDivElement | null>
  values: TestFormValues | undefined
  documents?: DocumentClientAreaDto[]
  onSubmit: (values: TestFormValues) => Promise<void>
  goPrevStep: (values: TestFormValues) => void
  goNextStep: (values: TestFormValues) => void
  renderBeforeQuestions?: (section: TestSectionDto) => React.ReactNode
  noTitle?: boolean
}

type FooterState = {
  isSubmitting: boolean
  isValid: boolean
}

export const TestFormContent = forwardRef<TestFormRef, BaseProps>((props, ref) => {
  const { handleSubmit, setErrors, values } = useFormikContext<TestFormValues>()

  const { data, step, noTitle, documents, renderBeforeQuestions, goPrevStep } = props
  const { categorySizes } = useCategorySizesReadContext()
  const { t } = useTranslation()

  const currentSection = getCurrentSection(data, step)
  const allQuestions = useMemo(
    () => data.sections.flatMap((section) => section.questions),
    [data.sections]
  )

  useImperativeHandle(ref, () => ({
    goPrevStep() {
      setErrors({})
      props.goPrevStep(values)
    },
  }))

  return (
    <Form onSubmit={handleSubmit} className={styles.form} autoComplete='off'>
      {currentSection && (
        <Box key={currentSection.code} flex direction='column' gap='x16' pt='x8'>
          {!noTitle && (
            <Box align='center' justify={props.centered ? 'center' : 'flex-start'} gap='x8' flex>
              {!isFirstSection(data, step) && (
                <IconButton.Ghost icon={ArrowIcon.Left} onClick={() => goPrevStep(values)} />
              )}
              <Heading6 medium>
                {currentSection.name ||
                  t('Sign up.We are almost finished. Click next to continue.')}
              </Heading6>
            </Box>
          )}

          {renderBeforeQuestions && renderBeforeQuestions(currentSection)}

          <QuestionFieldFactory
            section={currentSection}
            allQuestions={allQuestions}
            categorySizes={categorySizes}
            documents={documents}
          />
        </Box>
      )}
    </Form>
  )
})

const TestFormFooterState: React.FC<{ onChange: (state: FooterState) => void }> = ({
  onChange,
}) => {
  const { isSubmitting, isValid } = useFormikContext<TestFormValues>()

  useEffect(() => {
    onChange({ isSubmitting, isValid })
  }, [isSubmitting, isValid, onChange])

  return null
}

export const TestForm = forwardRef<TestFormRef, BaseProps>((props, ref) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const formikRef = useRef<FormikProps<TestFormValues>>(null)
  const [footerState, setFooterState] = useState<FooterState>({
    isSubmitting: false,
    isValid: false,
  })

  const initialValues = useMemo<TestFormValues>(() => {
    return {
      clientId: account?.id ?? '',
      dateTaken: '',
      category: props.data?.category || '',
      testId: props.data?.id || '',
      selectedAnswers: props.values?.selectedAnswers || {},
      freeAnswers: props.values?.freeAnswers || {},
      documents: props.values?.documents || {},
    }
  }, [account?.id, props.data?.category, props.data?.id, props.values])

  const handleValidation = useCallback(
    (values: TestFormValues) => {
      const currentSection = getCurrentSection(props.data, props.step)
      return validateTestForm(
        currentSection ? [currentSection] : [],
        values,
        t,
        props.data.sections
      )
    },
    [props.data, props.step, t]
  )

  const handleFormSubmit = async (values: TestFormValues, { setSubmitting }: any) => {
    try {
      if (isLastSection(props.data, props.step)) {
        await props.onSubmit(dataToSubmit(values, props.data.sections))
      } else {
        props.goNextStep(dataToSubmit(values, props.data.sections))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const currentSectionIndex = getSectionIndex(props.data.sections, props.step)
  const activeIndex = currentSectionIndex >= 0 ? currentSectionIndex : 0
  const formKey = props.step

  return (
    <>
      <AnimatedSwitch activeKey={props.step} activeIndex={activeIndex}>
        <Formik
          key={formKey}
          innerRef={formikRef}
          initialValues={initialValues}
          validate={handleValidation}
          onSubmit={handleFormSubmit}
          enableReinitialize
          validateOnMount
          validateOnChange
          validateOnBlur
        >
          <>
            <TestFormContent {...props} ref={ref} />
            <TestFormFooterState onChange={setFooterState} />
          </>
        </Formik>
      </AnimatedSwitch>
      <FullScreenFooter contentRef={props.contentRef}>
        <Button.Primary
          size='lg'
          fullWidth
          onClick={() => formikRef.current?.submitForm()}
          loading={footerState.isSubmitting}
          disabled={!footerState.isValid || footerState.isSubmitting}
        >
          {isLastSection(props.data, props.step) ? t('Submit') : t('Next')}
        </Button.Primary>
      </FullScreenFooter>
    </>
  )
})

const getSectionIndex = (sections: readonly TestSectionDto[], step: string): number => {
  return sections.findIndex((section) => section.code === step)
}

const getCurrentSection = (data: ClientTestDto, step: string): TestSectionDto | undefined => {
  const sectionIndex = getSectionIndex(data.sections, step)
  return sectionIndex >= 0 ? data.sections[sectionIndex] : undefined
}

const isLastSection = (data: ClientTestDto, step: string) => {
  const sectionIndex = getSectionIndex(data.sections, step)
  return sectionIndex >= 0 && sectionIndex === data.sections.length - 1
}

const isFirstSection = (data: ClientTestDto, step: string) => {
  return getSectionIndex(data.sections, step) === 0
}

const dataToSubmit = (
  values: TestFormValues,
  sections: readonly TestSectionDto[]
): TestFormValues => {
  const { freeAnswers, selectedAnswers } = getCleanedAnswers(
    values.selectedAnswers,
    values.freeAnswers,
    sections
  )
  return { ...values, selectedAnswers, freeAnswers }
}

export const TestFormWrapper: React.FC<PropsWithChildren<{ isLoading?: boolean }>> = (props) => (
  <div className={styles.wrapper}>
    <Loading showLoadingIcon isLoading={props.isLoading ?? false}>
      {props.children}
    </Loading>
  </div>
)
